import { Link } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  FileCheck2,
  ShieldCheck,
  Landmark,
  ReceiptText,
  BadgeCheck,
  Building2,
  X,
  Maximize2,
} from "lucide-react";

/**
 * Company Legal Documents (view-only, no download).
 * Shows the 4 statutory certificates of Samara Wiz Private Limited as elegantly
 * framed images. The certificate images themselves are the originals — unchanged.
 * Click any card to open a full-screen lightbox for a closer look. No PDFs, no
 * downloads.
 */

const DOCS = [
  {
    key: "incorporation",
    icon: Building2,
    tag: "MCA · Registrar of Companies",
    title: "Certificate of Incorporation",
    subtitle: "Ministry of Corporate Affairs, Government of India",
    number: "CIN · U46497UP2026PTC255480",
    img: "/legal-docs/incorporation.png",
    accent: "from-royal via-royal-deep to-royal",
  },
  {
    key: "pan",
    icon: BadgeCheck,
    tag: "Income Tax Department",
    title: "Company PAN Card",
    subtitle: "Digitally-signed e-PAN — Income Tax Department, GoI",
    number: "PAN · ABUCS2969J",
    img: "/legal-docs/pan.png",
    accent: "from-royal via-plum to-royal",
  },
  {
    key: "tan",
    icon: ReceiptText,
    tag: "Income Tax · TDS Registration",
    title: "TAN Allotment Letter",
    subtitle: "Section 203A of the Income-tax Act, 1961",
    number: "TAN · MRTS41457D",
    img: "/legal-docs/tan.png",
    accent: "from-royal-deep via-royal to-royal-deep",
  },
  {
    key: "gst",
    icon: Landmark,
    tag: "Goods & Services Tax · Uttar Pradesh",
    title: "GST Registration Certificate",
    subtitle: "Form GST REG-06 — GSTN, Government of India",
    number: "GSTIN · 09ABUCS2969J1ZB",
    img: "/legal-docs/gst.png",
    accent: "from-royal via-royal-deep to-plum",
  },
];

/* --- ORNAMENT: gold-corner brackets so every certificate feels framed --- */
function CornerBracket({ position }) {
  const map = {
    tl: "top-2 left-2 border-l-2 border-t-2 rounded-tl-sm",
    tr: "top-2 right-2 border-r-2 border-t-2 rounded-tr-sm",
    bl: "bottom-2 left-2 border-l-2 border-b-2 rounded-bl-sm",
    br: "bottom-2 right-2 border-r-2 border-b-2 rounded-br-sm",
  };
  return (
    <span
      className={`absolute w-6 h-6 border-gold pointer-events-none ${map[position]}`}
    />
  );
}

function DocCard({ doc, index, onOpen }) {
  const Icon = doc.icon;
  return (
    <article
      data-testid={`legal-doc-${doc.key}`}
      className="group relative bg-white rounded-sm overflow-hidden border border-line hover:border-gold/60 transition-all shadow-[0_1px_2px_rgba(11,61,145,0.04)] hover:shadow-[0_8px_28px_rgba(11,61,145,0.12)]"
    >
      {/* Royal header strip */}
      <div className={`relative px-6 py-5 bg-gradient-to-r ${doc.accent}`}>
        <div className="absolute inset-0 opacity-[0.15] mix-blend-overlay [background-image:repeating-linear-gradient(45deg,#fff_0_1px,transparent_1px_8px)]" />
        <div className="relative flex items-start gap-4">
          <div className="w-11 h-11 grid place-items-center bg-white/12 border border-white/25 rounded-sm backdrop-blur-sm shrink-0">
            <Icon size={20} className="text-gold" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[9px] tracking-[0.3em] uppercase text-gold/90 mb-1">
              {doc.tag}
            </div>
            <h3 className="font-serif text-lg sm:text-xl text-white leading-snug">
              {doc.title}
            </h3>
            <p className="text-[11px] text-white/70 italic mt-0.5">
              {doc.subtitle}
            </p>
          </div>
          <div className="hidden sm:block text-right shrink-0">
            <div className="text-[9px] tracking-[0.3em] uppercase text-gold/80">
              Document
            </div>
            <div className="font-serif text-lg text-white/95">
              #{String(index + 1).padStart(2, "0")}
            </div>
          </div>
        </div>
      </div>

      {/* Certificate framed area */}
      <div className="p-5 sm:p-6 bg-gradient-to-b from-ivory/40 to-white">
        <button
          type="button"
          data-testid={`legal-doc-${doc.key}-open`}
          onClick={() => onOpen(doc)}
          className="relative block w-full group/frame focus:outline-none"
        >
          {/* Outer gold frame */}
          <div className="relative bg-white p-2.5 sm:p-3 border-2 border-gold/40 rounded-sm shadow-[inset_0_0_0_1px_rgba(11,61,145,0.08)]">
            {/* Inner navy hairline frame */}
            <div className="relative bg-white border border-royal/25 rounded-sm overflow-hidden">
              <img
                src={doc.img}
                alt={doc.title}
                loading="lazy"
                className="block w-full h-auto object-contain transition-transform duration-500 group-hover/frame:scale-[1.015]"
              />
              {/* Corner brackets */}
              <CornerBracket position="tl" />
              <CornerBracket position="tr" />
              <CornerBracket position="bl" />
              <CornerBracket position="br" />
            </div>

            {/* Zoom hint */}
            <div className="absolute top-4 right-4 opacity-0 group-hover/frame:opacity-100 transition-opacity bg-royal text-white text-[10px] tracking-widest uppercase px-2.5 py-1.5 rounded-sm flex items-center gap-1.5 shadow-lg">
              <Maximize2 size={11} /> Zoom
            </div>
          </div>
        </button>

        {/* Meta row */}
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-dashed border-line">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-navy/45">
              Reference
            </div>
            <div className="font-mono text-sm text-royal font-semibold tracking-wide mt-0.5">
              {doc.number}
            </div>
          </div>
          <div className="flex items-center gap-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-sm">
            <ShieldCheck size={13} />
            Verified · Govt. of India
          </div>
        </div>
      </div>
    </article>
  );
}

/* Full-screen zoom lightbox */
function Lightbox({ doc, onClose }) {
  if (!doc) return null;
  return (
    <div
      data-testid="legal-doc-lightbox"
      className="fixed inset-0 z-[80] bg-royal/95 backdrop-blur-sm grid place-items-center p-4 sm:p-8 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div className="absolute top-4 right-4 flex items-center gap-3">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gold/90">
          {doc.number}
        </div>
        <button
          data-testid="legal-doc-lightbox-close"
          onClick={onClose}
          className="w-10 h-10 grid place-items-center bg-white/10 hover:bg-white/20 border border-white/25 text-white rounded-sm transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>
      <div
        className="relative max-w-6xl max-h-[92vh] w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white p-3 sm:p-4 border-2 border-gold rounded-sm shadow-2xl">
          <img
            src={doc.img}
            alt={doc.title}
            className="block w-full h-auto max-h-[82vh] object-contain"
          />
        </div>
        <div className="mt-3 text-center text-xs text-white/70 italic">
          {doc.title} — {doc.subtitle}
        </div>
      </div>
    </div>
  );
}

export default function LegalDocuments() {
  const [zoom, setZoom] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        {/* Page header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 grid place-items-center bg-royal/[0.06] border border-royal/20 rounded-sm">
            <FileCheck2 size={24} className="text-royal" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-gold">
              Legal · Compliance
            </div>
            <h1
              data-testid="legal-documents-title"
              className="font-serif text-4xl lg:text-5xl text-royal"
            >
              Company Documents
            </h1>
          </div>
        </div>
        <p className="text-navy/70 leading-relaxed border-l-2 border-gold pl-4 italic mb-10 max-w-3xl">
          Samara Wiz Private Limited is a duly incorporated Indian company,
          registered with the Ministry of Corporate Affairs and holding all
          statutory tax registrations. Below are our four core certificates,
          displayed here for transparency. Click any document to view in full
          screen.
        </p>

        {/* Legal section tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-10">
          <Link
            to="/legal/documents"
            data-testid="legal-tab-documents"
            className="px-4 py-3 text-center rounded-sm bg-royal text-white border border-royal"
          >
            Documents
          </Link>
          <Link
            to="/legal/terms"
            data-testid="legal-tab-terms"
            className="px-4 py-3 text-center rounded-sm border border-line text-royal hover:border-gold transition-colors"
          >
            Terms
          </Link>
          <Link
            to="/legal/privacy"
            data-testid="legal-tab-privacy"
            className="px-4 py-3 text-center rounded-sm border border-line text-royal hover:border-gold transition-colors"
          >
            Privacy
          </Link>
          <Link
            to="/legal/refund"
            data-testid="legal-tab-refund"
            className="px-4 py-3 text-center rounded-sm border border-line text-royal hover:border-gold transition-colors"
          >
            Refund
          </Link>
        </div>

        {/* 4 documents */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-7">
          {DOCS.map((d, i) => (
            <DocCard doc={d} index={i} key={d.key} onOpen={setZoom} />
          ))}
        </div>

        {/* Bottom disclaimer */}
        <div
          data-testid="legal-documents-disclaimer"
          className="mt-14 p-6 bg-ivory border border-line rounded-sm text-xs text-navy/70 leading-relaxed"
        >
          <span className="font-semibold text-royal">Note:</span> The
          certificates shown above are the official records of Samara Wiz
          Private Limited as issued by the respective statutory authorities of
          the Government of India. They are presented on this page for public
          verification only — no downloadable copy is provided. For any legal
          or compliance matter, please write to{" "}
          <a
            href="mailto:legal@samarawiz.com"
            className="text-royal underline hover:text-gold"
          >
            legal@samarawiz.com
          </a>
          .
        </div>
      </section>

      <Lightbox doc={zoom} onClose={() => setZoom(null)} />
      <Footer />
    </div>
  );
}
